import { env } from 'next-runtime-env';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigation } from '@/contexts/NavigationContext';
import { useSession } from '@/lib/hooks/useSession';

// Time in seconds before expiry to show the warning
const WARNING_BEFORE_TIMEOUT_SECONDS = parseInt(env('NEXT_PUBLIC_SESSION_TIMEOUT_WARNING_SECONDS') || '300', 10);

const CHECK_INTERVAL = 1000; // 1 second
const SESSION_TIMEOUT_ATLAS = parseInt(env('NEXT_PUBLIC_SESSION_TIMEOUT_ATLAS') || '86400', 10);
const SESSION_TIMEOUT_EXTERNAL = parseInt(env('NEXT_PUBLIC_SESSION_TIMEOUT_EXTERNAL') || '7200', 10);

// Helper to get cookie value
const getCookieValue = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [cookieName, cookieValue] = cookie.trim().split('=');
    if (cookieName === name) {
      return decodeURIComponent(cookieValue);
    }
  }
  return null;
};

interface SessionTimeoutAlertProps {
  className?: string;
}

export const SessionTimeoutAlert = ({ className }: SessionTimeoutAlertProps) => {
  const { pathname } = useNavigation();
  const { data: session } = useSession();
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [lastReset, setLastReset] = useState(Date.now());
  const [hasInitialized, setHasInitialized] = useState(false);

  // --- BroadcastChannel setup ---
  const channelRef = useRef<BroadcastChannel | null>(null);
  if (!channelRef.current) {
    channelRef.current = new BroadcastChannel('session-sync');
  }

  // Debounce for activity reset
  const activityTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Store the cleanup function in a ref so it can be called from anywhere
  const cleanupActivityListeners = useRef<() => void>(() => {});

  // Initialize session timer when session data is first loaded (fresh login detection)
  useEffect(() => {
    if (session?.isAuthenticated && !hasInitialized) {
      console.log('🔄 Fresh login detected, initializing session timer');
      const now = Date.now();
      setLastReset(now);
      localStorage.setItem('sessionLastReset', String(now));
      setHasInitialized(true);
      // Broadcast to other tabs that we have a fresh login
      channelRef.current?.postMessage({ type: 'fresh_login', timestamp: now });
    }
  }, [session?.isAuthenticated, hasInitialized]);

  useEffect(() => {
    const handleActivity = () => {
      if (showWarning) return; // Do not reset timer if warning is showing
      if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current);
      activityTimeoutRef.current = setTimeout(() => {
        const now = Date.now();
        setLastReset(now);
        localStorage.setItem('sessionLastReset', String(now));
        channelRef.current?.postMessage({ type: 'reset', timestamp: now });
        setShowWarning(false);
      }, 200); // Debounce to avoid excessive resets
    };
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach(event => window.addEventListener(event, handleActivity));
    cleanupActivityListeners.current = () => {
      events.forEach(event => window.removeEventListener(event, handleActivity));
      if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current);
    };
    return cleanupActivityListeners.current;
  }, [showWarning]);

  const handleSignOut = useCallback(async () => {
    if (isSigningOut) return;
    setIsSigningOut(true);
    // Remove activity listeners immediately
    cleanupActivityListeners.current();

    try {
      // FIRST: Store current page for post-logout callback - MUST be before any navigation
      // Store as a persistent cookie that survives the entire logout/login cycle
      const currentPath = window.location.pathname + window.location.search;
      console.log('🚪 Debug - Session timeout logout started');
      console.log('📍 Debug - Current path to store as session timeout callback:', currentPath);

      // Set session timeout callback as a persistent cookie (not localStorage)
      // Base64 encode the path to avoid WAF triggers on patterns like "/delete"
      const encodedPath = btoa(currentPath);
      document.cookie = `session_timeout_callback=${encodedPath}; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`;
      console.log(
        '✅ Debug - Stored session timeout callback in cookie (30 days):',
        currentPath,
        '(base64:',
        encodedPath,
        ')',
      );

      // Step 1: Call server-side logout to clear httpOnly cookies and revoke tokens
      const logoutResponse = await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });

      const logoutData = await logoutResponse.json();
      console.log('Server logout response:', logoutData);

      // Step 2: Clear client-side storage and non-httpOnly cookies
      if (typeof window !== 'undefined' && window.localStorage) {
        // Don't clear all localStorage - just auth-related items, preserve post_logout_callback_url
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (
            key &&
            key !== 'post_logout_callback_url' &&
            (key.includes('okta') || key.includes('oauth') || key.includes('session'))
          ) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
      }

      // Clear client-side cookies (all Okta cookies are now HttpOnly and cleared server-side;
      // clear okta-logout-url here only for legacy sessions that may have had it set without HttpOnly)
      if (typeof document !== 'undefined') {
        const nonHttpOnlyCookies = ['okta-logout-url'];
        nonHttpOnlyCookies.forEach(cookieName => {
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
        });
      }

      // Step 3: Use logout URL from server response
      if (logoutData.oktaLogoutUrl) {
        console.log('Redirecting to Okta logout URL:', logoutData.oktaLogoutUrl);
        window.location.href = logoutData.oktaLogoutUrl;
      } else {
        console.error('No logout URL from server:', {
          hasIdToken: logoutData.hasIdToken,
          hasIssuer: logoutData.hasIssuer,
          logoutData,
        });
        window.location.href = '/auth/login';
      }
    } catch (error) {
      console.error('Session timeout logout error:', error);
      // Store callback URL even in error case (before navigation) - use cookie for persistence
      const currentPath = window.location.pathname + window.location.search;
      // Base64 encode the path to avoid WAF triggers on patterns like "/delete"
      const encodedPath = btoa(currentPath);
      document.cookie = `session_timeout_callback=${encodedPath}; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`;

      // Final fallback
      window.location.href = '/auth/login';
    }
  }, [isSigningOut]);

  // Store interval id in a ref so we can clear/restart it
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

  const startTimerInterval = useCallback(() => {
    // Don't start timer until component is properly initialized
    if (!hasInitialized || !session?.isAuthenticated) {
      console.log('⏸️ Skipping timer start - not initialized or not authenticated');
      return;
    }

    if (intervalIdRef.current) clearInterval(intervalIdRef.current);
    intervalIdRef.current = setInterval(() => {
      const now = Date.now();
      // Always re-read lastReset from localStorage for perfect sync
      const storedReset = Number(localStorage.getItem('sessionLastReset')) || lastReset;
      const isAtlasUser = session?.user?.email?.endsWith('@atlas-sp.com') || false;
      const sessionTimeoutValue = isAtlasUser ? SESSION_TIMEOUT_ATLAS : SESSION_TIMEOUT_EXTERNAL;
      const inactivityTimeRemaining = storedReset + sessionTimeoutValue * 1000 - now;
      const timeRemaining = Math.floor(inactivityTimeRemaining / 1000);

      // Add additional safety check to prevent immediate logout on fresh login
      if (timeRemaining <= WARNING_BEFORE_TIMEOUT_SECONDS && timeRemaining >= 0) {
        setShowWarning(true);
        setSecondsLeft(timeRemaining);
        if (timeRemaining === 0 && !isSigningOut) {
          console.log('⏰ Debug - Session timeout reached, triggering logout automatically');
          setTimeout(() => handleSignOut(), 0);
        }
      } else if (timeRemaining < 0 && !isSigningOut) {
        // Add safety check: only logout if the stored reset is significantly old
        // This prevents logout due to stale localStorage data
        const timeSinceReset = now - storedReset;
        const maxSessionTime = sessionTimeoutValue * 1000;

        if (timeSinceReset > maxSessionTime + 60000) {
          // Add 1 minute buffer
          console.log('⏰ Debug - Session expired (negative time), triggering logout after safety check');
          handleSignOut();
        } else {
          console.log('⚠️ Debug - Negative time detected but within safety buffer, resetting timer');
          const resetTime = Date.now();
          setLastReset(resetTime);
          localStorage.setItem('sessionLastReset', String(resetTime));
          channelRef.current?.postMessage({ type: 'reset', timestamp: resetTime });
        }
      } else {
        setShowWarning(false);
      }
    }, CHECK_INTERVAL);
  }, [session, lastReset, isSigningOut, handleSignOut, showWarning, hasInitialized]);

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (event.data?.type === 'logout') {
        // Other tabs: store session timeout callback in cookie for consistency
        const currentPath = window.location.pathname + window.location.search;
        // Base64 encode the path to avoid WAF triggers on patterns like "/delete"
        const encodedPath = btoa(currentPath);
        document.cookie = `session_timeout_callback=${encodedPath}; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`;
        handleSignOut();
      }
    },
    [handleSignOut],
  );

  // Listen for messages from other tabs
  useEffect(() => {
    const channel = channelRef.current;
    if (!channel) return;
    channel.onmessage = event => {
      if (event.data?.type === 'reset') {
        setLastReset(event.data.timestamp);
        localStorage.setItem('sessionLastReset', String(event.data.timestamp));
        setShowWarning(false);
        // Restart timer interval for perfect sync
        startTimerInterval();
      } else if (event.data?.type === 'fresh_login') {
        console.log('🔄 Fresh login detected from another tab, syncing session timer');
        setLastReset(event.data.timestamp);
        localStorage.setItem('sessionLastReset', String(event.data.timestamp));
        setShowWarning(false);
        setHasInitialized(true);
        // Restart timer interval for perfect sync
        startTimerInterval();
      } else {
        handleMessage(event);
      }
    };
  }, [startTimerInterval, handleMessage]);

  // Start timer on mount
  useEffect(() => {
    startTimerInterval();
    return () => {
      if (intervalIdRef.current) clearInterval(intervalIdRef.current);
    };
  }, [startTimerInterval]);

  // On tab wake, check session expiry
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && hasInitialized && session?.isAuthenticated) {
        const isAtlasUser = session?.user?.email?.endsWith('@atlas-sp.com') || false;
        const sessionTimeoutValue = isAtlasUser ? SESSION_TIMEOUT_ATLAS : SESSION_TIMEOUT_EXTERNAL;
        const storedReset = Number(localStorage.getItem('sessionLastReset')) || lastReset;
        const timeSinceReset = Date.now() - storedReset;
        const maxSessionTime = sessionTimeoutValue * 1000;

        // Add safety buffer to prevent logout due to stale data
        if (timeSinceReset > maxSessionTime + 60000) {
          // Add 1 minute buffer
          console.log('⏰ Tab visibility change - session expired, logging out');
          handleSignOut();
        } else if (timeSinceReset > maxSessionTime) {
          console.log('⚠️ Tab visibility change - session appears expired but within buffer, resetting');
          const resetTime = Date.now();
          setLastReset(resetTime);
          localStorage.setItem('sessionLastReset', String(resetTime));
          channelRef.current?.postMessage({ type: 'reset', timestamp: resetTime });
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [session, handleSignOut, lastReset, hasInitialized]);

  const resetSession = useCallback(async () => {
    try {
      const now = Date.now();
      setLastReset(now); // Reset inactivity timer
      localStorage.setItem('sessionLastReset', String(now));
      setShowWarning(false);
      channelRef.current?.postMessage({ type: 'reset', timestamp: now });
      // No API call needed - this is purely client-side inactivity tracking
    } catch (error) {
      await handleSignOut();
    }
  }, [handleSignOut]);

  if (pathname?.startsWith('/auth')) return null;

  if (!showWarning) return null;

  return (
    <div
      className={`fixed bottom-4 right-4 z-50 rounded-md border border-amber-300 bg-amber-50 p-4 shadow-lg ${className}`}>
      <h3 className="text-lg font-semibold text-amber-800">Session Timeout Warning</h3>
      <p className="mb-2 text-amber-700">Your session will expire in {secondsLeft} seconds.</p>
      <div className="flex gap-2">
        {secondsLeft > 0 && (
          <button
            onClick={resetSession}
            className="rounded bg-blue-600 px-3 py-1 font-medium text-white hover:bg-blue-700">
            Keep Session Active
          </button>
        )}
        <button
          onClick={handleSignOut}
          className="rounded bg-gray-200 px-3 py-1 font-medium text-gray-800 hover:bg-gray-300">
          Logout Now
        </button>
      </div>
    </div>
  );
};

export default SessionTimeoutAlert;
